import { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import '@/styles/backoffice-tokens.css';

/**
 * AppShell — the new Heirloom Ops back-office frame.
 * Provides a left sidebar (collapsible) + topbar + main content slot.
 * Scopes design tokens via the `.ops-shell` wrapper so marketing styles are unaffected.
 *
 * Props:
 *  - pageTitle: string shown in the topbar
 *  - subtitle: optional secondary text in the topbar
 *  - activeNav: string key identifying the currently selected sidebar item
 *  - onNavigate: (key) => void  — called when a non-routed sidebar item is clicked
 *  - role: 'admin' | 'interviewer' | 'production' — determines sidebar items
 *  - topbarActions: optional ReactNode rendered on the right of the topbar
 */
export default function AppShell({ pageTitle, subtitle, activeNav, onNavigate, role = 'admin', topbarActions, children }) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('ops_sidebar_collapsed') === '1';
  });

  useEffect(() => {
    try { localStorage.setItem('ops_sidebar_collapsed', collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  return (
    <div className="ops-shell min-h-screen flex" data-testid="ops-shell">
      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed(c => !c)}
        activeNav={activeNav}
        onNavigate={onNavigate}
        role={role}
      />
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar title={pageTitle} subtitle={subtitle} actions={topbarActions} />
        <main className="flex-1 min-w-0 bg-[#FAFBFC]">
          {children}
        </main>
      </div>
    </div>
  );
}
